import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useNavigate, useRouterState } from '@tanstack/react-router';
import { BookOpen, CalendarClock, ClipboardList, Flag, Info, SearchX, Sparkles, TriangleAlert, Wrench } from 'lucide-react';
import { useId, useState, type ReactElement, type ReactNode } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo, CampoSelecao } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Medida } from '@/componentes/Medida';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { RodapeDeContagem, RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { useBanner } from '@/lib/banner';
import { formatarDecimal, formatarInteiro } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { useSessao } from '@/modulos/sessao/consultas';

import { apiDaPreditiva, useFalhas, useInvestigacao, useInvestigacoes, useModelos, usePrevisoes, usePropostas, useRecorrencias, useResumoDaPreditiva, useRodadas } from './api';
import {
  GavetaDeClassificacao,
  GavetaDeModelo,
  GavetaDeProposta,
  GavetaDoCatalogo,
  JanelaDaBaseMinima,
  JanelaDeBacktest,
  JanelaDeConclusao,
  JanelaDeDecisao,
  JanelaDeDesfecho,
  JanelaDeInvestigacao,
  JanelaDeRetirada,
  type InicialDaProposta,
} from './JanelasDaPreditiva';
import {
  FILTROS_DA_FALHA,
  ROTULO_DA_ASSOCIACAO,
  ROTULO_DA_CATEGORIA,
  ROTULO_DO_FILTRO_DA_FALHA,
  ROTULO_DO_TIPO_DE_MODELO,
  ROTULO_DO_TIPO_DE_PROPOSTA,
  SITUACAO_DA_FALHA,
  SITUACAO_DA_PREVISAO,
  SITUACAO_DA_PROPOSTA,
  SITUACAO_DO_MODELO,
  backtestEscrito,
  fraseDaFalta,
  horas,
  horizonteEscrito,
  intervaloEscrito,
  numeroDaOrdem,
  parametrosEscritos,
  pct,
  placarEscrito,
  quando,
  retornoDaRodada,
  valorContraLimite,
  type FalhaNaLista,
  type FatorAssociado,
  type FiltroDaFalha,
  type GrupoDeRecorrencia,
  type Investigacao,
  type ModeloPreditivo,
  type Previsao,
  type PropostaTecnica,
  type ResultadoDaRodada,
  type Rodada,
  type SituacaoDaPrevisao,
  type SituacaoDaProposta,
} from './preditiva';
import { SeloDaPreditiva } from './Selo';

/**
 * Módulo 18 do v6 — MANUTENÇÃO PREDITIVA, na mesa (0105): "Manutenção ›
 * Falhas e preditiva".
 *
 * Um hub com quatro abas, cada uma com endereço próprio (p.40): o HISTÓRICO
 * de falhas (a corretiva e a classificação dela, numa gaveta à direita com
 * "Sugerir pela IA" em azul de consulta), as RECORRÊNCIAS (com a base mínima e
 * "associação, não causa", e as investigações), as PREVISÕES EM PILOTO (os
 * modelos, o placar com as falhas não previstas ao lado, e cada previsão) e as
 * PROPOSTAS técnicas (quem propõe não decide).
 *
 * Não há tela-modelo do módulo 18 no manual (o mapa das p.50 a p.60 não o
 * desenha): segue o hub com abas (p.40), a lista com detalhe (p.38) e a
 * aprovação (p.44) — dúvida de design registrada. Abaixo da base mínima, a
 * tela diz o que falta, em banner informativo, e nunca mostra número.
 */
const ABAS = [
  {
    para: '/manutencao/preditiva',
    rotulo: 'Histórico de falhas',
    explicacao:
      'Cada O.S. corretiva e a classificação dela: o componente, o modo e a categoria da causa. A O.S. não muda; a corretiva sem causa registrada fica fora da base.',
  },
  {
    para: '/manutencao/preditiva/recorrencias',
    rotulo: 'Recorrências',
    explicacao: 'A falha que volta no mesmo modelo de máquina, só com a base mínima. Os fatores são associação, não causa: a causa vem da investigação.',
  },
  {
    para: '/manutencao/preditiva/previsoes',
    rotulo: 'Previsões em piloto',
    explicacao: 'Os modelos medidos fora da amostra, o placar com as falhas não previstas ao lado, e cada previsão. Previsão não é diagnóstico.',
  },
  {
    para: '/manutencao/preditiva/propostas',
    rotulo: 'Propostas técnicas',
    explicacao: 'Inspeção ou ajuste de plano, com o fabricante citado. Quem propõe não decide, e nada muda o plano sozinho.',
  },
] as const;

const rotaDasFalhas = getRouteApi('/manutencao/preditiva');
const rotaDasRecorrencias = getRouteApi('/manutencao/preditiva/recorrencias');
const rotaDasPrevisoes = getRouteApi('/manutencao/preditiva/previsoes');
const rotaDasPropostas = getRouteApi('/manutencao/preditiva/propostas');

function abaDoCaminho(caminho: string): (typeof ABAS)[number] {
  return [...ABAS].reverse().find((a) => caminho === a.para || caminho.startsWith(`${a.para}/`)) ?? ABAS[0];
}

function usePermissoes() {
  const sessao = useSessao();
  const p = sessao.data?.permissoes ?? [];
  return {
    classificar: p.includes('falha.classificar'),
    sugerir: p.includes('falha.classificar') && p.includes('ia.perguntar'),
    catalogo: p.includes('catalogo_de_falha.gerir'),
    investigar: p.includes('investigacao.gravar'),
    gerirModelo: p.includes('modelo_preditivo.gerir'),
    adotar: p.includes('modelo_preditivo.adotar'),
    decidir: p.includes('proposta_tecnica.decidir'),
  };
}

function ErroDaLista({ oQue, erro, aoTentar }: { readonly oQue: string; readonly erro: unknown; readonly aoTentar: () => void }) {
  return (
    <EstadoVazio
      icone={TriangleAlert}
      tom="erro"
      titulo={`Não foi possível carregar ${oQue}`}
      descricao={explicarFalha(erro).comoResolver}
      acao={
        <Botao nivel="secundario" onClick={aoTentar}>
          Carregar de novo
        </Botao>
      }
    />
  );
}

/* ================================================================ o hub */

export function HubDaPreditiva({ children, acoes }: { readonly children: ReactNode; readonly acoes?: ReactNode }) {
  const caminho = useRouterState({ select: (e) => e.location.pathname });
  const navegar = useNavigate();
  const aba = abaDoCaminho(caminho);
  const resumo = useResumoDaPreditiva();
  const pode = usePermissoes();
  const [catalogo, setCatalogo] = useState(false);
  const [base, setBase] = useState(false);
  const r = resumo.data;
  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Manutenção"
          titulo="Falhas e preditiva"
          subtitulo="O histórico de falhas padronizado, a recorrência e os modelos em piloto — com a base mínima e o que falta"
          acoes={
            <>
              <Botao nivel="secundario" onClick={() => { setBase(true); }}>
                Base mínima
              </Botao>
              <Botao nivel="secundario" onClick={() => { setCatalogo(true); }}>
                <BookOpen aria-hidden="true" className="size-[var(--icone-controle)]" />
                Catálogo de falhas
              </Botao>
              {acoes}
            </>
          }
        />
        <FaixaIndicadores>
          <CardIndicador
            rotulo="Corretivas a classificar"
            valor={r ? formatarInteiro(r.aClassificar) : ''}
            contexto={r ? `de ${formatarInteiro(r.corretivas)} corretivas; ${formatarInteiro(r.classificadas)} classificadas` : 'com diagnóstico e causa'}
            icone={ClipboardList}
            tom="standby"
            carregando={!r}
            aoAbrir={() => { void navegar({ to: '/manutencao/preditiva', search: { situacao: 'a_classificar' } }); }}
          />
          <CardIndicador
            rotulo="Sem causa registrada"
            valor={r ? formatarInteiro(r.semCausa) : ''}
            contexto="não classificáveis: ficam fora da base"
            icone={TriangleAlert}
            tom="neutro"
            carregando={!r}
            aoAbrir={() => { void navegar({ to: '/manutencao/preditiva', search: { situacao: 'sem_causa' } }); }}
          />
          <CardIndicador
            rotulo="Acerto da sugestão da IA"
            valor={r ? (r.acertoDaIa.pct === null ? '—' : formatarDecimal(r.acertoDaIa.pct, 1).replace(/,0$/, '')) : ''}
            {...(r?.acertoDaIa.pct !== null && r ? { unidade: '%' } : {})}
            contexto={r ? (r.acertoDaIa.sugestoes === 0 ? 'nenhuma sugestão conferida ainda' : `o componente certo em ${formatarInteiro(r.acertoDaIa.acertos)} de ${formatarInteiro(r.acertoDaIa.sugestoes)}`) : 'sugestão ao lado da decisão'}
            icone={Sparkles}
            tom="disponivel"
            carregando={!r}
          />
          <CardIndicador
            rotulo="Propostas para decidir"
            valor={r ? formatarInteiro(r.propostasAbertas) : ''}
            contexto={r ? `${formatarInteiro(r.previsoesAbertas)} previsões abertas · ${formatarInteiro(r.modelosEmPiloto)} modelos em piloto` : 'quem propõe não decide'}
            icone={Flag}
            tom="marca"
            carregando={!r}
            aoAbrir={() => { void navegar({ to: '/manutencao/preditiva/propostas', search: { situacao: 'proposta' } }); }}
          />
        </FaixaIndicadores>
        {/* O TRILHO DE ABAS SAIU (pedido do dono, 25/10/2026: *"Eu não quero
            mais abas internas"*). As telas deste módulo são seções da
            navegação lateral, que é a navegação do sistema. A linha que
            explica a tela aberta ficou: ela diz o que esta tela responde, e
            não era parte do trilho. */}
        <p className="txt-apoio max-w-[80ch] text-pretty text-tinta-secundaria">{aba.explicacao}</p>
        {children}
      </div>
      <GavetaDoCatalogo aberta={catalogo} podeGerir={pode.catalogo} aoFechar={() => { setCatalogo(false); }} />
      <JanelaDaBaseMinima key={String(base)} base={r?.base ?? null} aberta={base} podeMudar={pode.adotar} aoFechar={() => { setBase(false); }} />
    </div>
  );
}

/* ====================================================== 1. o histórico */

const COLUNAS_DAS_FALHAS: readonly Coluna<FalhaNaLista>[] = [
  {
    chave: 'os',
    rotulo: 'O.S.',
    celula: (f) => (
      <span className="flex flex-col">
        <span className="codigo text-tinta">{numeroDaOrdem(f.numero)}</span>
        <TextoDaCelula texto={quando(f.abertaEm)} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  {
    chave: 'maquina',
    rotulo: 'Máquina',
    celula: (f) => (
      <span className="flex flex-col">
        <span className="codigo text-tinta">{f.equipamento}</span>
        <TextoDaCelula texto={f.sintoma} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  {
    chave: 'componente',
    rotulo: 'Componente e modo',
    celula: (f) =>
      f.componente ? (
        <span className="flex flex-col">
          <span className="txt-corpo text-tinta">{`${f.componenteCodigo ?? ''} · ${f.componente}`}</span>
          <TextoDaCelula texto={f.modo ?? 'Sem modo'} classe="txt-apoio text-tinta-secundaria" />
        </span>
      ) : (
        <span className="txt-apoio text-tinta-secundaria">—</span>
      ),
  },
  {
    chave: 'categoria',
    rotulo: 'Causa',
    prioridade: 'secundaria',
    celula: (f) => <span className="txt-corpo text-tinta">{f.categoria ? ROTULO_DA_CATEGORIA[f.categoria] : '—'}</span>,
  },
  {
    chave: 'leitura',
    rotulo: 'Leitura na falha',
    alinhamento: 'numero',
    prioridade: 'secundaria',
    celula: (f) => (f.leituraNaFalha === null ? <span className="txt-apoio text-tinta-secundaria">—</span> : <Medida valor={formatarDecimal(f.leituraNaFalha, 1)} unidade="h" />),
  },
  { chave: 'situacao', rotulo: 'Situação', alinhamento: 'status', celula: (f) => <SeloDaPreditiva {...SITUACAO_DA_FALHA[f.situacao]} /> },
];

export function PaginaHistoricoDeFalhas() {
  const busca = rotaDasFalhas.useSearch();
  const navegar = rotaDasFalhas.useNavigate();
  const situacao: FiltroDaFalha = busca.situacao ?? 'todas';
  const pagina = busca.pagina ?? 1;
  const falhas = useFalhas(situacao, pagina);
  const resumo = useResumoDaPreditiva();
  const pode = usePermissoes();
  const [aberta, setAberta] = useState<string | null>(null);
  const linhas = falhas.data?.falhas ?? [];
  const total = falhas.data?.total ?? 0;
  const porPagina = falhas.data?.porPagina ?? 100;
  const contagem = resumo.data
    ? { todas: resumo.data.corretivas, a_classificar: resumo.data.aClassificar, classificada: resumo.data.classificadas, sem_causa: resumo.data.semCausa, em_diagnostico: resumo.data.emDiagnostico }
    : null;
  const ir = (s: FiltroDaFalha, p: number) => {
    void navegar({ search: { ...(s === 'todas' ? {} : { situacao: s }), ...(p > 1 ? { pagina: p } : {}) } });
  };
  return (
    <>
      <HubDaPreditiva>
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-72 max-w-full">
            <CampoSelecao
              rotulo="Situação"
              valor={situacao}
              aplicado={situacao !== 'todas'}
              aoMudar={(v) => { ir(v as FiltroDaFalha, 1); }}
              opcoes={FILTROS_DA_FALHA.map((f) => ({ valor: f, rotulo: contagem ? `${ROTULO_DO_FILTRO_DA_FALHA[f]} (${formatarInteiro(contagem[f])})` : ROTULO_DO_FILTRO_DA_FALHA[f] }))}
            />
          </div>
        </div>
        <Tabela
          rotulo="Histórico de falhas"
          colunas={COLUNAS_DAS_FALHAS}
          linhas={linhas}
          chaveDaLinha={(f) => f.ordemId}
          alturaDupla
          selecionada={aberta}
          aoSelecionar={(f) => { setAberta(f.ordemId); }}
          carregando={falhas.isPending}
          erro={falhas.isError ? <ErroDaLista oQue="o histórico de falhas" erro={falhas.error} aoTentar={() => { void falhas.refetch(); }} /> : null}
          vazio={
            situacao === 'todas' ? (
              <EstadoVazio
                icone={Wrench}
                titulo="Nenhuma O.S. corretiva ainda"
                descricao="O histórico de falhas nasce das O.S. corretivas da oficina. Quando a primeira tiver diagnóstico e causa raiz, ela aparece aqui para classificar."
              />
            ) : (
              <EstadoVazio
                icone={SearchX}
                titulo={`Nenhuma corretiva em ${ROTULO_DO_FILTRO_DA_FALHA[situacao].toLowerCase()}`}
                descricao="Limpe o filtro para ver o histórico inteiro."
                acao={
                  <Botao nivel="secundario" onClick={() => { ir('todas', 1); }}>
                    Limpar filtro
                  </Botao>
                }
              />
            )
          }
          rodape={
            <RodapeTabela
              primeiro={total === 0 ? 0 : (pagina - 1) * porPagina + 1}
              ultimo={Math.min(pagina * porPagina, total)}
              total={total}
              pagina={pagina}
              paginas={Math.max(1, Math.ceil(total / porPagina))}
              porPagina={porPagina}
              opcoesPorPagina={[porPagina]}
              aoMudarPagina={(p) => { ir(situacao, p); }}
              aoMudarPorPagina={() => undefined}
            />
          }
        />
      </HubDaPreditiva>
      <GavetaDeClassificacao ordemId={aberta} podeClassificar={pode.classificar} podeSugerir={pode.sugerir} aoFechar={() => { setAberta(null); }} />
    </>
  );
}

/* ====================================================== 2. a recorrência */

const COLUNAS_DA_RECORRENCIA: readonly Coluna<GrupoDeRecorrencia>[] = [
  {
    chave: 'componente',
    rotulo: 'Componente e modo',
    celula: (g) => (
      <span className="flex flex-col">
        <span className="txt-corpo text-tinta">{`${g.componenteCodigo} · ${g.componente}`}</span>
        <TextoDaCelula texto={g.modo ?? 'Sem modo'} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  { chave: 'modelo', rotulo: 'Modelo', celula: (g) => <TextoDaCelula texto={g.modelo} classe="txt-corpo text-tinta" /> },
  {
    chave: 'falhas',
    rotulo: 'Falhas',
    alinhamento: 'numero',
    celula: (g) => (
      <span className="flex flex-col items-end">
        <span className="numero text-tinta">{formatarInteiro(g.falhas)}</span>
        <span className="txt-apoio text-tinta-secundaria">{`em ${formatarInteiro(g.maquinas)} ${g.maquinas === 1 ? 'máquina' : 'máquinas'}`}</span>
      </span>
    ),
  },
  {
    chave: 'taxa',
    rotulo: 'Por 1.000 h',
    alinhamento: 'numero',
    celula: (g) => (g.taxaPorMilH === null ? <span className="txt-apoio text-tinta-secundaria">—</span> : <span className="numero text-tinta">{formatarDecimal(g.taxaPorMilH, 2)}</span>),
  },
  { chave: 'mtbf', rotulo: 'MTBF', alinhamento: 'numero', prioridade: 'secundaria', celula: (g) => <span className="numero text-tinta">{horas(g.mtbf.valor)}</span> },
  { chave: 'mttr', rotulo: 'MTTR', alinhamento: 'numero', prioridade: 'secundaria', celula: (g) => <span className="numero text-tinta">{horas(g.mttr.valor)}</span> },
  {
    chave: 'situacao',
    rotulo: 'Base',
    alinhamento: 'status',
    celula: (g) => <SeloDaPreditiva rotulo={g.recorrencia.suficiente ? 'Recorrência' : 'Amostra insuficiente'} tom={g.recorrencia.suficiente ? 'atencao' : 'neutro'} />,
  },
];

const COLUNAS_DAS_INVESTIGACOES: readonly Coluna<Investigacao>[] = [
  {
    chave: 'titulo',
    rotulo: 'Investigação',
    celula: (i) => (
      <span className="flex flex-col">
        <span className="txt-corpo text-tinta">{i.titulo}</span>
        <TextoDaCelula texto={[i.componente, i.modelo].filter(Boolean).join(' · ') || 'Sem escopo'} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  { chave: 'evidencias', rotulo: 'O.S.', alinhamento: 'numero', celula: (i) => <span className="numero text-tinta">{formatarInteiro(i.evidencias)}</span> },
  { chave: 'aberta', rotulo: 'Aberta em', prioridade: 'secundaria', celula: (i) => <TextoDaCelula texto={`${quando(i.abertaEm)}${i.abertaPor ? ` · ${i.abertaPor}` : ''}`} classe="txt-apoio text-tinta-secundaria" /> },
  {
    chave: 'situacao',
    rotulo: 'Situação',
    alinhamento: 'status',
    celula: (i) => <SeloDaPreditiva rotulo={i.situacao === 'aberta' ? 'Aberta' : i.situacao === 'concluida' ? 'Concluída' : 'Descartada'} tom={i.situacao === 'aberta' ? 'informacao' : i.situacao === 'concluida' ? 'sucesso' : 'neutro'} />,
  },
];

function TabelaDeFatores({ titulo, fatores, comExposicao }: { readonly titulo: string; readonly fatores: readonly FatorAssociado[]; readonly comExposicao: boolean }) {
  return (
    <div className="flex flex-col gap-2">
      <h4 className="txt-rotulo-campo">{titulo}</h4>
      <ul className="flex flex-col">
        {fatores.map((f) => (
          <li key={f.chave} className="flex items-baseline justify-between gap-3 border-t border-borda-sutil py-2 first:border-t-0">
            <span className="txt-corpo text-tinta">{f.rotulo}</span>
            <span className="txt-apoio text-right tabular-nums text-tinta-secundaria">
              {`${formatarInteiro(f.falhas)} falhas · ${pct(f.pctFalhas)} das falhas${comExposicao ? ` · ${f.pctExposicao === null ? 'sem alocação' : `${pct(f.pctExposicao)} dos dias de máquina`}` : ''}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PaginaRecorrencias() {
  const busca = rotaDasRecorrencias.useSearch();
  const navegar = rotaDasRecorrencias.useNavigate();
  const id = useId();
  const recorrencias = useRecorrencias(busca.de, busca.ate);
  const investigacoes = useInvestigacoes();
  const pode = usePermissoes();
  const [aberto, setAberto] = useState<string | null>(null);
  const [investigacaoAberta, setInvestigacaoAberta] = useState<string | null>(null);
  const [investigando, setInvestigando] = useState<GrupoDeRecorrencia | null>(null);
  const [concluindo, setConcluindo] = useState<Investigacao | null>(null);
  const [propondo, setPropondo] = useState<InicialDaProposta | null>(null);
  const r = recorrencias.data;
  const grupos = r?.grupos ?? [];
  const grupo = grupos.find((g) => g.chave === aberto) ?? null;
  const investigacao = (investigacoes.data?.investigacoes ?? []).find((i) => i.id === investigacaoAberta) ?? null;
  const detalheDaInvestigacao = useInvestigacao(investigacaoAberta);
  /* AMOSTRA INSUFICIENTE, em banner informativo: vale para a tela inteira. */
  useBanner(
    r && grupos.length > 0 && r.recorrentes === 0
      ? {
          id: `preditiva-recorrencia:${String(grupos.length)}`,
          gravidade: 'informativo',
          icone: Info,
          texto: `Amostra insuficiente: nenhum dos ${formatarInteiro(grupos.length)} grupos de falha atinge a base mínima da empresa (${formatarInteiro(r.base.falhasMinimasRecorrencia)} falhas em ${formatarInteiro(r.base.maquinasMinimasRecorrencia)} máquinas do mesmo modelo). Cada linha diz o que falta.`,
          rotuloDeFechar: 'Fechar o aviso de amostra insuficiente',
        }
      : null,
  );
  return (
    <>
      <HubDaPreditiva>
        <div className="flex flex-wrap items-end gap-4">
          <Campo id={`${id}-de`} rotulo="De">
            <input id={`${id}-de`} type="date" className="controle" value={busca.de ?? r?.de ?? ''} onChange={(e) => { void navegar({ search: { ...(busca.ate ? { ate: busca.ate } : {}), ...(e.target.value ? { de: e.target.value } : {}) } }); }} />
          </Campo>
          <Campo id={`${id}-ate`} rotulo="Até">
            <input id={`${id}-ate`} type="date" className="controle" value={busca.ate ?? r?.ate ?? ''} onChange={(e) => { void navegar({ search: { ...(busca.de ? { de: busca.de } : {}), ...(e.target.value ? { ate: e.target.value } : {}) } }); }} />
          </Campo>
          {r ? <p className="txt-apoio pb-2 text-tinta-secundaria">{`${formatarInteiro(r.falhas)} falhas classificadas no período · ${formatarInteiro(r.recorrentes)} grupos na base mínima`}</p> : null}
        </div>
        <Tabela
          rotulo="Recorrências"
          colunas={COLUNAS_DA_RECORRENCIA}
          linhas={grupos}
          chaveDaLinha={(g) => g.chave}
          alturaDupla
          selecionada={aberto}
          aoSelecionar={(g) => { setAberto(g.chave); }}
          carregando={recorrencias.isPending}
          erro={recorrencias.isError ? <ErroDaLista oQue="as recorrências" erro={recorrencias.error} aoTentar={() => { void recorrencias.refetch(); }} /> : null}
          vazio={
            <EstadoVazio
              icone={ClipboardList}
              titulo="Nenhuma falha classificada no período"
              descricao="A recorrência nasce do histórico padronizado. Classifique as corretivas no Histórico de falhas, ou escolha um período maior."
            />
          }
          rodape={<RodapeDeContagem total={grupos.length} />}
        />
        {r && !r.premissas.completo ? (
          <p className="txt-apoio max-w-[80ch] text-pretty text-tinta-secundaria">{`Fora da conta: ${r.premissas.naoInclui.join('; ')}.`}</p>
        ) : null}
        <section className="flex flex-col gap-3">
          <h2 className="txt-secao text-tinta">Investigações</h2>
          <Tabela
            rotulo="Investigações"
            colunas={COLUNAS_DAS_INVESTIGACOES}
            linhas={investigacoes.data?.investigacoes ?? []}
            chaveDaLinha={(i) => i.id}
            alturaDupla
            selecionada={investigacaoAberta}
            aoSelecionar={(i) => { setInvestigacaoAberta(i.id); }}
            carregando={investigacoes.isPending}
            erro={investigacoes.isError ? <ErroDaLista oQue="as investigações" erro={investigacoes.error} aoTentar={() => { void investigacoes.refetch(); }} /> : null}
            vazio={<EstadoVazio icone={SearchX} titulo="Nenhuma investigação aberta" descricao="Abra uma investigação a partir de uma recorrência: a hipótese, as O.S. como evidência e, depois, a conclusão." />}
            rodape={<RodapeDeContagem total={investigacoes.data?.investigacoes.length ?? 0} />}
          />
        </section>
      </HubDaPreditiva>
      {grupo ? (
        <PainelDetalhe
          key={grupo.chave}
          codigo={grupo.componenteCodigo}
          titulo={`${grupo.componente}${grupo.modo ? ` · ${grupo.modo}` : ''}`}
          subtitulo={grupo.modelo}
          marcadores={<SeloDaPreditiva rotulo={grupo.recorrencia.suficiente ? 'Recorrência' : 'Amostra insuficiente'} tom={grupo.recorrencia.suficiente ? 'atencao' : 'neutro'} />}
          dadosChave={
            <>
              <DadoChave rotulo="Falhas">{`${formatarInteiro(grupo.falhas)} em ${formatarInteiro(grupo.maquinas)} máquinas`}</DadoChave>
              <DadoChave rotulo="Por 1.000 h de uso">{grupo.taxaPorMilH === null ? 'Sem base' : formatarDecimal(grupo.taxaPorMilH, 2)}</DadoChave>
              <DadoChave rotulo="MTBF">{grupo.mtbf.valor === null ? 'Sem base' : `${horas(grupo.mtbf.valor)} (${formatarInteiro(grupo.mtbf.base)} intervalos)`}</DadoChave>
              <DadoChave rotulo="MTTR">{grupo.mttr.valor === null ? 'Sem base' : `${horas(grupo.mttr.valor)} (${formatarInteiro(grupo.mttr.base)} reparos)`}</DadoChave>
            </>
          }
          abas={[
            {
              id: 'base',
              rotulo: 'Base',
              conteudo: (
                <div className="flex flex-col gap-3">
                  {grupo.recorrencia.suficiente ? (
                    <p className="txt-corpo text-pretty text-tinta">{`A falha se repete: ${formatarInteiro(grupo.falhas)} vezes em ${formatarInteiro(grupo.maquinas)} máquinas do ${grupo.modelo}. É recorrência, não causa: a causa sai da investigação.`}</p>
                  ) : (
                    <p className="txt-corpo text-pretty text-tinta">{fraseDaFalta(grupo.recorrencia, 'chamar de recorrência')}</p>
                  )}
                  {!grupo.taxa.suficiente ? <p className="txt-apoio text-pretty text-tinta-secundaria">{fraseDaFalta(grupo.taxa, 'a taxa por 1.000 h')}</p> : null}
                  {[...grupo.mtbf.premissas.naoInclui, ...grupo.mttr.premissas.naoInclui].map((x) => (
                    <p key={x} className="txt-apoio text-pretty text-tinta-secundaria">{`Fora da conta: ${x}.`}</p>
                  ))}
                </div>
              ),
            },
            {
              id: 'associacao',
              rotulo: ROTULO_DA_ASSOCIACAO,
              conteudo:
                grupo.porCategoria && grupo.porObra ? (
                  <div className="flex flex-col gap-4">
                    <p className="txt-apoio text-pretty text-tinta-secundaria">
                      {`${ROTULO_DA_ASSOCIACAO}: a parte das falhas ao lado da parte dos dias de máquina. Diferença grande pede investigação, e não conclusão.`}
                    </p>
                    <TabelaDeFatores titulo="Pela categoria da causa" fatores={grupo.porCategoria} comExposicao={false} />
                    <TabelaDeFatores titulo="Pela obra do dia da falha" fatores={grupo.porObra} comExposicao />
                  </div>
                ) : (
                  <p className="txt-apoio text-pretty text-tinta-secundaria">Abaixo da base mínima, a associação seria ruído: a tela não a mostra.</p>
                ),
            },
            {
              id: 'os',
              rotulo: 'As O.S.',
              conteudo: <p className="txt-corpo text-pretty text-tinta">{grupo.ordens.map((n) => numeroDaOrdem(n)).join(', ')}</p>,
            },
          ]}
          acoes={
            pode.investigar
              ? [
                  <Botao key="investigar" nivel="primario" onClick={() => { setInvestigando(grupo); }}>
                    Abrir investigação
                  </Botao>,
                  <Botao key="propor" nivel="secundario" onClick={() => { setPropondo({ componenteId: grupo.componenteId, tipo: 'ajuste_de_plano' }); }}>
                    Propor ajuste
                  </Botao>,
                ]
              : []
          }
          {...(pode.investigar ? {} : { notaDasAcoes: 'Quem abre investigação e propõe é o perfil Planejador de manutenção.' })}
          aoFechar={() => { setAberto(null); }}
        />
      ) : null}
      {investigacao ? (
        <PainelDetalhe
          key={investigacao.id}
          titulo={investigacao.titulo}
          subtitulo={[investigacao.componente, investigacao.modelo].filter(Boolean).join(' · ') || 'Sem escopo'}
          marcadores={<SeloDaPreditiva rotulo={investigacao.situacao === 'aberta' ? 'Aberta' : investigacao.situacao === 'concluida' ? 'Concluída' : 'Descartada'} tom={investigacao.situacao === 'aberta' ? 'informacao' : 'sucesso'} />}
          dadosChave={
            <>
              <DadoChave rotulo="Aberta">{`${quando(investigacao.abertaEm)}${investigacao.abertaPor ? ` · ${investigacao.abertaPor}` : ''}`}</DadoChave>
              <DadoChave rotulo="Encerrada">{investigacao.encerradaEm ? `${quando(investigacao.encerradaEm)}${investigacao.encerradaPor ? ` · ${investigacao.encerradaPor}` : ''}` : 'Aberta'}</DadoChave>
            </>
          }
          abas={[
            {
              id: 'hipotese',
              rotulo: 'Hipótese',
              conteudo: (
                <div className="flex flex-col gap-3">
                  <p className="txt-corpo text-pretty text-tinta">{investigacao.hipotese}</p>
                  {investigacao.conclusao ? <p className="txt-corpo text-pretty text-tinta">{`Conclusão: ${investigacao.conclusao}`}</p> : null}
                </div>
              ),
            },
            {
              id: 'evidencias',
              rotulo: 'Evidências',
              conteudo: !detalheDaInvestigacao.data ? (
                <span aria-hidden="true" className="esqueleto block h-4 w-2/3 rounded-etiqueta" />
              ) : (
                <ul className="flex flex-col">
                  {detalheDaInvestigacao.data.evidencias.map((e) => (
                    <li key={e.id} className="flex flex-col border-t border-borda-sutil py-2 first:border-t-0">
                      <span className="txt-corpo text-tinta">{`${numeroDaOrdem(e.numero)} · ${e.equipamento} · ${quando(e.falhouEm)}`}</span>
                      <span className="txt-apoio text-tinta-secundaria">{[e.componente, e.categoria ? ROTULO_DA_CATEGORIA[e.categoria] : null, e.observacao].filter(Boolean).join(' · ') || 'Sem classificação'}</span>
                    </li>
                  ))}
                </ul>
              ),
            },
          ]}
          acoes={
            pode.investigar && investigacao.situacao === 'aberta'
              ? [
                  <Botao key="concluir" nivel="primario" onClick={() => { setConcluindo(investigacao); }}>
                    Encerrar investigação
                  </Botao>,
                  <Botao key="propor" nivel="secundario" onClick={() => { setPropondo({ investigacaoId: investigacao.id, ...(investigacao.componenteId ? { componenteId: investigacao.componenteId } : {}) }); }}>
                    Propor a partir dela
                  </Botao>,
                ]
              : []
          }
          aoFechar={() => { setInvestigacaoAberta(null); }}
        />
      ) : null}
      {pode.investigar ? (
        <>
          <JanelaDeInvestigacao key={investigando?.chave ?? 'nenhuma'} grupo={investigando} aberta={investigando !== null} aoFechar={() => { setInvestigando(null); }} />
          <JanelaDeConclusao key={concluindo?.id ?? 'nenhuma'} alvo={concluindo} aoFechar={() => { setConcluindo(null); }} />
          <GavetaDeProposta key={JSON.stringify(propondo)} aberta={propondo !== null} inicial={propondo ?? {}} aoFechar={() => { setPropondo(null); }} />
        </>
      ) : null}
    </>
  );
}

/* ====================================================== 3. as previsões */

const COLUNAS_DOS_MODELOS: readonly Coluna<ModeloPreditivo>[] = [
  {
    chave: 'nome',
    rotulo: 'Modelo',
    celula: (m) => (
      <span className="flex flex-col">
        <span className="txt-corpo text-tinta">{`${m.nome} · versão ${formatarInteiro(m.versao)}`}</span>
        <TextoDaCelula texto={ROTULO_DO_TIPO_DE_MODELO[m.tipo]} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  {
    chave: 'alvo',
    rotulo: 'Alvo',
    celula: (m) => (
      <span className="flex flex-col">
        <span className="txt-corpo text-tinta">{m.componente}</span>
        <TextoDaCelula texto={m.modelo} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  { chave: 'placar', rotulo: 'Placar', celula: (m) => <TextoDaCelula texto={placarEscrito(m.placar)} classe="txt-apoio text-tinta" /> },
  { chave: 'precisao', rotulo: 'Precisão', alinhamento: 'numero', celula: (m) => <span className="numero text-tinta">{pct(m.placar?.precisaoPct ?? null)}</span> },
  { chave: 'situacao', rotulo: 'Situação', alinhamento: 'status', celula: (m) => <SeloDaPreditiva {...SITUACAO_DO_MODELO[m.situacao]} /> },
];

const COLUNAS_DAS_PREVISOES: readonly Coluna<Previsao>[] = [
  {
    chave: 'maquina',
    rotulo: 'Máquina',
    celula: (p) => (
      <span className="flex flex-col">
        <span className="codigo text-tinta">{p.equipamento}</span>
        <TextoDaCelula texto={p.componente} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  { chave: 'valor', rotulo: 'Medido e limite', celula: (p) => <span className="txt-corpo tabular-nums text-tinta">{valorContraLimite(p)}</span> },
  { chave: 'horizonte', rotulo: 'Horizonte', prioridade: 'secundaria', celula: (p) => <TextoDaCelula texto={horizonteEscrito(p)} classe="txt-apoio text-tinta" /> },
  { chave: 'modelo', rotulo: 'Modelo', prioridade: 'secundaria', celula: (p) => <TextoDaCelula texto={`${p.modelo} · v${formatarInteiro(p.versao)}`} classe="txt-apoio text-tinta-secundaria" /> },
  { chave: 'emitida', rotulo: 'Emitida em', celula: (p) => <span className="txt-corpo tabular-nums text-tinta">{quando(p.emitidaEm)}</span> },
  { chave: 'situacao', rotulo: 'Desfecho', alinhamento: 'status', celula: (p) => <SeloDaPreditiva {...SITUACAO_DA_PREVISAO[p.situacao]} /> },
];

export function PaginaPrevisoesEmPiloto() {
  const busca = rotaDasPrevisoes.useSearch();
  const navegar = rotaDasPrevisoes.useNavigate();
  const situacao: 'todas' | SituacaoDaPrevisao = busca.situacao ?? 'todas';
  const pagina = busca.pagina ?? 1;
  const modelos = useModelos();
  const previsoes = usePrevisoes(situacao, pagina);
  const pode = usePermissoes();
  const [modeloAberto, setModeloAberto] = useState<string | null>(null);
  const [previsaoAberta, setPrevisaoAberta] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [medindo, setMedindo] = useState<ModeloPreditivo | null>(null);
  const [retirando, setRetirando] = useState<ModeloPreditivo | null>(null);
  const [desfecho, setDesfecho] = useState<Previsao | null>(null);
  const [propondo, setPropondo] = useState<InicialDaProposta | null>(null);
  const lista = modelos.data?.modelos ?? [];
  const modelo = lista.find((m) => m.id === modeloAberto) ?? null;
  const rodadas = useRodadas(modeloAberto);
  const linhas = previsoes.data?.previsoes ?? [];
  const previsao = linhas.find((p) => p.id === previsaoAberta) ?? null;
  const total = previsoes.data?.total ?? 0;
  const porPagina = previsoes.data?.porPagina ?? 100;
  const semBase = modelos.data ? lista.length > 0 && lista.every((m) => m.backtest?.abstencao) : false;
  useBanner(
    semBase
      ? {
          id: 'preditiva-modelos-sem-base',
          gravidade: 'informativo',
          icone: Info,
          texto: 'Amostra insuficiente: o backtest de todos os modelos se absteve. A previsão espera o histórico classificado crescer; o motivo de cada um está no detalhe.',
          rotuloDeFechar: 'Fechar o aviso de amostra insuficiente',
        }
      : null,
  );
  const ir = (s: 'todas' | SituacaoDaPrevisao, p: number) => {
    void navegar({ search: { ...(s === 'todas' ? {} : { situacao: s }), ...(p > 1 ? { pagina: p } : {}) } });
  };
  return (
    <>
      <HubDaPreditiva
        {...(pode.gerirModelo
          ? {
              acoes: (
                <Botao nivel="primario" onClick={() => { setCriando(true); }}>
                  Novo modelo
                </Botao>
              ),
            }
          : {})}
      >
        <section className="flex flex-col gap-3">
          <h2 className="txt-secao text-tinta">Modelos</h2>
          <Tabela
            rotulo="Modelos preditivos"
            colunas={COLUNAS_DOS_MODELOS}
            linhas={lista}
            chaveDaLinha={(m) => m.id}
            alturaDupla
            selecionada={modeloAberto}
            aoSelecionar={(m) => { setModeloAberto(m.id); }}
            carregando={modelos.isPending}
            erro={modelos.isError ? <ErroDaLista oQue="os modelos" erro={modelos.error} aoTentar={() => { void modelos.refetch(); }} /> : null}
            vazio={
              <EstadoVazio
                icone={CalendarClock}
                titulo="Nenhum modelo preditivo"
                descricao={
                  pode.gerirModelo
                    ? 'Crie o primeiro modelo para um componente de um modelo de máquina. Ele só vai a piloto depois do backtest, e só é adotado com o placar na base mínima.'
                    : 'Quem cria e roda os modelos é o perfil Administrador, até o dono decidir.'
                }
              />
            }
            rodape={<RodapeDeContagem total={lista.length} />}
          />
        </section>
        <section className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 className="txt-secao text-tinta">Previsões</h2>
            <div className="w-64 max-w-full">
              <CampoSelecao
                rotulo="Desfecho"
                valor={situacao}
                aplicado={situacao !== 'todas'}
                aoMudar={(v) => { ir(v as 'todas' | SituacaoDaPrevisao, 1); }}
                opcoes={[{ valor: 'todas', rotulo: 'Todas' }, ...Object.entries(SITUACAO_DA_PREVISAO).map(([v, s]) => ({ valor: v, rotulo: s.rotulo }))]}
              />
            </div>
          </div>
          <Tabela
            rotulo="Previsões em piloto"
            colunas={COLUNAS_DAS_PREVISOES}
            linhas={linhas}
            chaveDaLinha={(p) => p.id}
            alturaDupla
            selecionada={previsaoAberta}
            aoSelecionar={(p) => { setPrevisaoAberta(p.id); }}
            carregando={previsoes.isPending}
            erro={previsoes.isError ? <ErroDaLista oQue="as previsões" erro={previsoes.error} aoTentar={() => { void previsoes.refetch(); }} /> : null}
            vazio={
              situacao === 'todas' ? (
                <EstadoVazio icone={CalendarClock} titulo="Nenhuma previsão emitida" descricao="As previsões saem das rodadas dos modelos em piloto, a pedido. Cada rodada emite com evidência ou se abstém dizendo o que falta." />
              ) : (
                <EstadoVazio
                  icone={SearchX}
                  titulo={`Nenhuma previsão ${SITUACAO_DA_PREVISAO[situacao].rotulo.toLowerCase()}`}
                  descricao="Limpe o filtro para ver todas."
                  acao={
                    <Botao nivel="secundario" onClick={() => { ir('todas', 1); }}>
                      Limpar filtro
                    </Botao>
                  }
                />
              )
            }
            rodape={
              <RodapeTabela
                primeiro={total === 0 ? 0 : (pagina - 1) * porPagina + 1}
                ultimo={Math.min(pagina * porPagina, total)}
                total={total}
                pagina={pagina}
                paginas={Math.max(1, Math.ceil(total / porPagina))}
                porPagina={porPagina}
                opcoesPorPagina={[porPagina]}
                aoMudarPagina={(p) => { ir(situacao, p); }}
                aoMudarPorPagina={() => undefined}
              />
            }
          />
        </section>
      </HubDaPreditiva>
      {modelo ? (
        <DetalheDoModelo
          key={modelo.id}
          m={modelo}
          rodadas={rodadas.data?.rodadas ?? null}
          podeGerir={pode.gerirModelo}
          podeAdotar={pode.adotar}
          aoMedir={() => { setMedindo(modelo); }}
          aoRetirar={() => { setRetirando(modelo); }}
          aoFechar={() => { setModeloAberto(null); }}
        />
      ) : null}
      {previsao ? (
        <PainelDetalhe
          key={previsao.id}
          codigo={previsao.equipamento}
          titulo={`${previsao.componente} na ${previsao.equipamento}`}
          subtitulo={`${previsao.modelo} · versão ${formatarInteiro(previsao.versao)}`}
          marcadores={<SeloDaPreditiva {...SITUACAO_DA_PREVISAO[previsao.situacao]} />}
          dadosChave={
            <>
              <DadoChave rotulo="Medido e limite">{valorContraLimite(previsao)}</DadoChave>
              <DadoChave rotulo="Horizonte">{horizonteEscrito(previsao)}</DadoChave>
              <DadoChave rotulo="Emitida em">{quando(previsao.emitidaEm)}</DadoChave>
              <DadoChave rotulo="Desfecho">
                {previsao.situacao === 'aberta'
                  ? 'Aberta'
                  : `${SITUACAO_DA_PREVISAO[previsao.situacao].rotulo}${previsao.desfechoNumero ? ` · ${numeroDaOrdem(previsao.desfechoNumero)}` : ''}${previsao.antecedencia !== null ? ` · ${formatarDecimal(previsao.antecedencia, 1).replace(/,0$/, '')} ${previsao.unidadeDoHorizonte === 'h' ? 'h' : 'dias'} antes` : ''}`}
              </DadoChave>
            </>
          }
          abas={[
            {
              id: 'evidencia',
              rotulo: 'Evidência',
              conteudo: (
                <div className="flex flex-col gap-2">
                  {previsao.evidencia.fatos.map((f) => (
                    <p key={f} className="txt-corpo text-pretty text-tinta">{f}</p>
                  ))}
                  <p className="txt-apoio text-pretty text-tinta-secundaria">Previsão em piloto não é diagnóstico: confira a máquina e registre o desfecho, que conta no placar do modelo.</p>
                </div>
              ),
            },
            {
              id: 'premissas',
              rotulo: 'Premissas',
              conteudo: (
                <ul className="flex list-disc flex-col gap-1 pl-4">
                  {previsao.evidencia.premissas.inclui.map((x) => (
                    <li key={x} className="txt-apoio text-tinta">{x}</li>
                  ))}
                  {previsao.evidencia.premissas.naoInclui.map((x) => (
                    <li key={x} className="txt-apoio text-tinta-secundaria">{`Fora: ${x}`}</li>
                  ))}
                </ul>
              ),
            },
            ...(previsao.desfechoNota ? [{ id: 'nota', rotulo: 'Nota', conteudo: <p className="txt-corpo text-pretty text-tinta">{previsao.desfechoNota}</p> }] : []),
          ]}
          acoes={[
            ...(pode.gerirModelo && previsao.situacao === 'aberta'
              ? [
                  <Botao key="desfecho" nivel="primario" onClick={() => { setDesfecho(previsao); }}>
                    Registrar desfecho
                  </Botao>,
                ]
              : []),
            ...(pode.investigar
              ? [
                  <Botao
                    key="inspecao"
                    nivel="secundario"
                    onClick={() => { setPropondo({ tipo: 'inspecao', equipamentoId: previsao.equipamentoId, equipamento: previsao.equipamento, componenteId: previsao.componenteId, previsaoId: previsao.id }); }}
                  >
                    Propor inspeção
                  </Botao>,
                ]
              : []),
          ]}
          aoFechar={() => { setPrevisaoAberta(null); }}
        />
      ) : null}
      {pode.gerirModelo ? (
        <>
          <GavetaDeModelo key={String(criando)} aberta={criando} sugeridos={modelos.data?.sugeridos ?? null} aoFechar={() => { setCriando(false); }} />
          <JanelaDeBacktest key={medindo?.id ?? 'nenhum'} alvo={medindo} aoFechar={() => { setMedindo(null); }} />
          <JanelaDeRetirada key={retirando?.id ?? 'nenhum'} alvo={retirando} aoFechar={() => { setRetirando(null); }} />
          <JanelaDeDesfecho key={desfecho?.id ?? 'nenhuma'} alvo={desfecho} aoFechar={() => { setDesfecho(null); }} />
        </>
      ) : null}
      {pode.investigar ? <GavetaDeProposta key={JSON.stringify(propondo)} aberta={propondo !== null} inicial={propondo ?? {}} aoFechar={() => { setPropondo(null); }} /> : null}
    </>
  );
}

function DetalheDoModelo({
  m,
  rodadas,
  podeGerir,
  podeAdotar,
  aoMedir,
  aoRetirar,
  aoFechar,
}: {
  readonly m: ModeloPreditivo;
  readonly rodadas: readonly Rodada[] | null;
  readonly podeGerir: boolean;
  readonly podeAdotar: boolean;
  readonly aoMedir: () => void;
  readonly aoRetirar: () => void;
  readonly aoFechar: () => void;
}) {
  /* As três gravações do detalhe do modelo, com o retorno de cada uma (R04). */
  const cliente = useQueryClient();
  const invalidar = () => {
    void cliente.invalidateQueries({ queryKey: ['preditiva'] });
  };
  const piloto = useMutation({
    mutationFn: () => apiDaPreditiva.piloto(m.id),
    meta: { retorno: retornoDe<{ nome: string }>((r) => ({ titulo: 'Modelo em piloto', alvo: { nome: r.nome }, efeito: 'Roda a pedido; cada previsão conta no placar' })), silenciarFalha: true },
    onSuccess: invalidar,
  });
  const rodada = useMutation({
    mutationFn: () => apiDaPreditiva.rodada(m.id),
    meta: { retorno: retornoDe<ResultadoDaRodada>((r) => ({ titulo: 'Rodada concluída', ...retornoDaRodada(r) })), silenciarFalha: true },
    onSuccess: invalidar,
  });
  const adotar = useMutation({
    mutationFn: () => apiDaPreditiva.adotar(m.id),
    meta: { retorno: retornoDe<{ nome: string }>((r) => ({ titulo: 'Modelo adotado', alvo: { nome: r.nome }, efeito: 'Passou da base mínima da empresa; o placar continua contando' })), silenciarFalha: true },
    onSuccess: invalidar,
  });
  const p = m.placar;
  const emRascunho = m.situacao === 'rascunho';
  const roda = m.situacao === 'em_piloto' || m.situacao === 'adotado';
  const primaria =
    emRascunho && podeGerir ? (
      m.backtest && !m.backtest.abstencao ? (
        <Botao key="piloto" nivel="primario" carregando={piloto.isPending} onClick={() => { piloto.mutate(); }}>
          Pôr em piloto
        </Botao>
      ) : (
        <Botao key="backtest" nivel="primario" onClick={aoMedir}>
          Rodar backtest
        </Botao>
      )
    ) : roda && podeGerir ? (
      <Botao key="rodada" nivel="primario" carregando={rodada.isPending} onClick={() => { rodada.mutate(); }}>
        Rodar previsões
      </Botao>
    ) : null;
  const secundaria =
    m.situacao === 'em_piloto' && podeAdotar ? (
      <Botao key="adotar" nivel="secundario" disabled={!m.adocao?.suficiente} title={m.adocao && !m.adocao.suficiente ? 'O placar ainda não atinge a base mínima.' : undefined} carregando={adotar.isPending} onClick={() => { adotar.mutate(); }}>
        Adotar modelo
      </Botao>
    ) : m.situacao !== 'retirado' && podeGerir ? (
      <Botao key="retirar" nivel="secundario" onClick={aoRetirar}>
        Retirar modelo
      </Botao>
    ) : null;
  const erro = piloto.error ?? rodada.error ?? adotar.error;
  return (
    <PainelDetalhe
      codigo={`v${formatarInteiro(m.versao)}`}
      titulo={m.nome}
      subtitulo={`${m.componente}${m.modo ? ` · ${m.modo}` : ''} · ${m.modelo}`}
      marcadores={<SeloDaPreditiva {...SITUACAO_DO_MODELO[m.situacao]} />}
      dadosChave={
        <>
          <DadoChave rotulo="Tipo">{ROTULO_DO_TIPO_DE_MODELO[m.tipo]}</DadoChave>
          <DadoChave rotulo="Precisão no piloto">{pct(p?.precisaoPct ?? null)}</DadoChave>
          <DadoChave rotulo="Falhas não previstas">{formatarInteiro(p?.falhasNaoPrevistas ?? 0)}</DadoChave>
          <DadoChave rotulo="Última rodada">{m.ultimaRodada ? quando(m.ultimaRodada) : 'Nenhuma'}</DadoChave>
        </>
      }
      abas={[
        {
          id: 'placar',
          rotulo: 'Placar',
          conteudo: (
            <div className="flex flex-col gap-3">
              {erro ? <p role="alert" className="txt-corpo text-pretty text-erro-texto">{`${explicarFalha(erro).erro}. ${explicarFalha(erro).comoResolver}`}</p> : null}
              <dl className="flex flex-col">
                {[
                  ['Emitidas', p?.emitidas],
                  ['Abertas', p?.abertas],
                  ['Confirmadas', p?.confirmadas],
                  ['Falsos alertas', p?.falsosAlertas],
                  ['Expiradas', p?.expiradas],
                  ['Falhas não previstas', p?.falhasNaoPrevistas],
                ].map(([r, n]) => (
                  <div key={String(r)} className="flex items-baseline justify-between gap-3 border-t border-borda-sutil py-2 first:border-t-0">
                    <dt className="txt-apoio text-tinta-secundaria">{r}</dt>
                    <dd className="txt-corpo tabular-nums text-tinta">{formatarInteiro(Number(n ?? 0))}</dd>
                  </div>
                ))}
              </dl>
              <p className="txt-apoio text-pretty text-tinta-secundaria">
                {m.adocao?.suficiente ? 'O placar atinge a base mínima da empresa: o modelo pode ser adotado.' : fraseDaFalta(m.adocao ?? { suficiente: false, falta: [] }, 'adotar')}
              </p>
            </div>
          ),
        },
        {
          id: 'backtest',
          rotulo: 'Backtest',
          conteudo: (
            <div className="flex flex-col gap-2">
              <p className="txt-corpo text-pretty text-tinta">{backtestEscrito(m.backtest)}</p>
              <p className="txt-apoio text-pretty text-tinta-secundaria">{parametrosEscritos(m)}</p>
              {m.backtest?.premissas.inclui.map((x) => (
                <p key={x} className="txt-apoio text-pretty text-tinta-secundaria">{x}</p>
              ))}
            </div>
          ),
        },
        {
          id: 'rodadas',
          rotulo: 'Rodadas',
          conteudo:
            rodadas === null ? (
              <span aria-hidden="true" className="esqueleto block h-4 w-2/3 rounded-etiqueta" />
            ) : rodadas.length === 0 ? (
              <p className="txt-apoio text-tinta-secundaria">Nenhuma rodada ainda.</p>
            ) : (
              <ul className="flex flex-col">
                {rodadas.map((r) => (
                  <li key={r.id} className="flex flex-col gap-1 border-t border-borda-sutil py-2 first:border-t-0">
                    <span className="txt-corpo text-tinta">{`${quando(r.executadaEm)} · ${formatarInteiro(r.emitidas)} emitidas de ${formatarInteiro(r.avaliadas)} máquinas`}</span>
                    {r.abstencoes.slice(0, 5).map((a) => (
                      <span key={`${a.codigo ?? 'familia'}-${a.motivo}`} className="txt-apoio text-pretty text-tinta-secundaria">{`${a.codigo ?? 'Família'}: ${a.motivo}`}</span>
                    ))}
                    {r.abstencoes.length > 5 ? <span className="txt-apoio text-tinta-secundaria">{`e mais ${formatarInteiro(r.abstencoes.length - 5)} abstenções`}</span> : null}
                  </li>
                ))}
              </ul>
            ),
        },
      ]}
      acoes={[primaria, secundaria].filter((x): x is ReactElement => x !== null)}
      aoFechar={aoFechar}
    />
  );
}

/* ====================================================== 4. as propostas */

const COLUNAS_DAS_PROPOSTAS: readonly Coluna<PropostaTecnica>[] = [
  {
    chave: 'tipo',
    rotulo: 'Proposta',
    celula: (p) => (
      <span className="flex flex-col">
        <span className="txt-corpo text-tinta">{ROTULO_DO_TIPO_DE_PROPOSTA[p.tipo]}</span>
        <TextoDaCelula texto={p.tipo === 'inspecao' ? (p.equipamento ?? '—') : `${p.plano ?? ''} · ${p.tarefa ?? ''}`} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  {
    chave: 'intervalo',
    rotulo: 'Intervalo',
    alinhamento: 'numero',
    celula: (p) =>
      p.tipo === 'ajuste_de_plano' ? (
        <span className="txt-corpo tabular-nums text-tinta">{`${intervaloEscrito(p.intervaloAtual, p.gatilho)} → ${intervaloEscrito(p.intervaloProposto, p.gatilho)}`}</span>
      ) : (
        <span className="txt-apoio text-tinta-secundaria">—</span>
      ),
  },
  {
    chave: 'fabricante',
    rotulo: 'Fabricante',
    prioridade: 'secundaria',
    celula: (p) =>
      p.contrariaFabricante ? <SeloDaPreditiva rotulo="Contraria o fabricante" tom="atencao" /> : <TextoDaCelula texto={p.documento ? `${p.documento}${p.pagina ? `, p. ${formatarInteiro(p.pagina)}` : ''}` : 'Sem documento citado'} classe="txt-apoio text-tinta-secundaria" />,
  },
  { chave: 'quem', rotulo: 'Proposta por', prioridade: 'secundaria', celula: (p) => <TextoDaCelula texto={`${p.propostaPor ?? '—'} · ${quando(p.propostaEm)}`} classe="txt-apoio text-tinta" /> },
  { chave: 'situacao', rotulo: 'Situação', alinhamento: 'status', celula: (p) => <SeloDaPreditiva {...SITUACAO_DA_PROPOSTA[p.situacao]} /> },
];

export function PaginaPropostasTecnicas() {
  const busca = rotaDasPropostas.useSearch();
  const navegar = rotaDasPropostas.useNavigate();
  const situacao: 'todas' | SituacaoDaProposta = busca.situacao ?? 'todas';
  const propostas = usePropostas(situacao);
  const pode = usePermissoes();
  const [aberta, setAberta] = useState<string | null>(null);
  const [nova, setNova] = useState(false);
  const [decidindo, setDecidindo] = useState<PropostaTecnica | null>(null);
  const linhas = propostas.data?.propostas ?? [];
  const eu = propostas.data?.eu ?? '';
  const p = linhas.find((x) => x.id === aberta) ?? null;
  return (
    <>
      <HubDaPreditiva
        {...(pode.investigar
          ? {
              acoes: (
                <Botao nivel="primario" onClick={() => { setNova(true); }}>
                  Nova proposta
                </Botao>
              ),
            }
          : {})}
      >
        <div className="w-64 max-w-full">
          <CampoSelecao
            rotulo="Situação"
            valor={situacao}
            aplicado={situacao !== 'todas'}
            aoMudar={(v) => { void navegar({ search: v === 'todas' ? {} : { situacao: v as SituacaoDaProposta } }); }}
            opcoes={[{ valor: 'todas', rotulo: 'Todas' }, ...Object.entries(SITUACAO_DA_PROPOSTA).map(([v, s]) => ({ valor: v, rotulo: s.rotulo }))]}
          />
        </div>
        <Tabela
          rotulo="Propostas técnicas"
          colunas={COLUNAS_DAS_PROPOSTAS}
          linhas={linhas}
          chaveDaLinha={(x) => x.id}
          alturaDupla
          selecionada={aberta}
          aoSelecionar={(x) => { setAberta(x.id); }}
          carregando={propostas.isPending}
          erro={propostas.isError ? <ErroDaLista oQue="as propostas" erro={propostas.error} aoTentar={() => { void propostas.refetch(); }} /> : null}
          vazio={
            situacao === 'todas' ? (
              <EstadoVazio icone={Flag} titulo="Nenhuma proposta técnica" descricao="A proposta nasce da investigação ou da previsão: uma inspeção, ou um ajuste de plano com o fabricante citado." />
            ) : (
              <EstadoVazio
                icone={SearchX}
                titulo={`Nenhuma proposta ${SITUACAO_DA_PROPOSTA[situacao].rotulo.toLowerCase()}`}
                descricao="Limpe o filtro para ver todas."
                acao={
                  <Botao nivel="secundario" onClick={() => { void navegar({ search: {} }); }}>
                    Limpar filtro
                  </Botao>
                }
              />
            )
          }
          rodape={<RodapeDeContagem total={linhas.length} />}
        />
      </HubDaPreditiva>
      {p ? (
        <PainelDetalhe
          key={p.id}
          titulo={`${ROTULO_DO_TIPO_DE_PROPOSTA[p.tipo]}${p.equipamento ? ` · ${p.equipamento}` : ''}`}
          subtitulo={p.tipo === 'inspecao' ? (p.componente ?? 'Sem componente') : `${p.plano ?? ''} · ${p.tarefa ?? ''}`}
          marcadores={
            <>
              <SeloDaPreditiva {...SITUACAO_DA_PROPOSTA[p.situacao]} />
              {p.contrariaFabricante ? <SeloDaPreditiva rotulo="Contraria o fabricante" tom="atencao" /> : null}
            </>
          }
          dadosChave={
            <>
              <DadoChave rotulo="Proposta por">{`${p.propostaPor ?? '—'} · ${quando(p.propostaEm)}`}</DadoChave>
              <DadoChave rotulo="Intervalo">{p.tipo === 'ajuste_de_plano' ? `${intervaloEscrito(p.intervaloAtual, p.gatilho)} → ${intervaloEscrito(p.intervaloProposto, p.gatilho)}` : '—'}</DadoChave>
              <DadoChave rotulo="Fabricante">{p.documento ? `${p.documento}${p.pagina ? `, p. ${formatarInteiro(p.pagina)}` : ''}` : 'Sem documento citado'}</DadoChave>
              <DadoChave rotulo="Decisão">{p.decididaPor ? `${p.decididaPor} · ${quando(p.decididaEm)}` : 'Para decidir'}</DadoChave>
            </>
          }
          abas={[
            { id: 'justificativa', rotulo: 'Justificativa', conteudo: <p className="txt-corpo text-pretty text-tinta">{p.justificativa}</p> },
            {
              id: 'decisao',
              rotulo: 'Decisão',
              conteudo: (
                <div className="flex flex-col gap-2">
                  <p className="txt-corpo text-pretty text-tinta">{p.notaDaDecisao ?? 'Ainda não decidida. Quem propõe não decide.'}</p>
                  {p.ordemNumero ? <p className="txt-apoio text-tinta-secundaria">{`Gerou a ${numeroDaOrdem(p.ordemNumero)} de inspeção.`}</p> : null}
                  {p.revisaoPlanoId ? <p className="txt-apoio text-tinta-secundaria">Abriu a revisão do plano em rascunho: o intervalo muda quando alguém a publicar com a nota.</p> : null}
                </div>
              ),
            },
          ]}
          acoes={
            pode.decidir && p.situacao === 'proposta' && p.propostaPorId !== eu
              ? [
                  <Botao key="decidir" nivel="primario" onClick={() => { setDecidindo(p); }}>
                    Decidir proposta
                  </Botao>,
                ]
              : []
          }
          {...(p.situacao === 'proposta' && p.propostaPorId === eu
            ? { notaDasAcoes: 'Você propôs: quem propõe não decide.' }
            : p.situacao === 'proposta' && !pode.decidir
              ? { notaDasAcoes: 'Quem decide a proposta técnica é o perfil Administrador, até o dono decidir.' }
              : {})}
          aoFechar={() => { setAberta(null); }}
        />
      ) : null}
      {pode.investigar ? <GavetaDeProposta key={String(nova)} aberta={nova} inicial={{}} aoFechar={() => { setNova(false); }} /> : null}
      {pode.decidir ? <JanelaDeDecisao key={decidindo?.id ?? 'nenhuma'} alvo={decidindo} eu={eu} aoFechar={() => { setDecidindo(null); }} /> : null}
    </>
  );
}
